import { useState, useEffect } from "react";

import { useMe } from "@/entities/profile";
import { useToast } from "@/shared/hooks/useToast";
import { Roles, type User } from "@/entities/user";
import {
  useSetNotificationMethod,
  useUpdatePassword,
  useUpdateUsername,
} from "@/entities/profile";
import {
  useHeadContact,
  useUpsertHeadContact,
} from "@/entities/head-contacs/api/api";

export const useProfileForm = (user: User | null) => {
  const [username, setUsername] = useState(user ? user.username : "");
  const [password, setPassword] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);

  const [headContact, setHeadContact] = useState("");
  const { data: headContactFromApi } = useHeadContact();

  const { mutateAsync: updateUsername, status: updateUsernameStatus } =
    useUpdateUsername();
  const { mutateAsync: updatePassword, status: updatePasswordStatus } =
    useUpdatePassword();
  const {
    mutateAsync: setNotificationMethod,
    status: setNotificationMethodStatus,
  } = useSetNotificationMethod();
  const { data: userData, refetch: refetchMe } = useMe();
  const { mutateAsync: upsertHeadContact } = useUpsertHeadContact();
  const [notificationType, setNotificationType] = useState(
    userData ? userData.tgIsPreffered : "panel",
  );

  const { toast } = useToast();

  useEffect(() => {
    if (!userData) return;
    else {
      setNotificationType(userData.tgIsPreferred ? "telegram" : "panel");
    }
  }, [userData]);

  useEffect(() => {
    if (headContactFromApi && user?.role === Roles.Head) {
      setHeadContact(headContactFromApi.data.data.contact);
    }
  }, [headContactFromApi, user?.role]);

  const handleUpdate = async () => {
    if (username !== user?.username) {
      await updateUsername({ username });
    }

    if (password !== "") {
      await updatePassword({ password });
    }

    if (notificationType !== (userData?.tgIsPreferred ? "telegram" : "panel")) {
      await setNotificationMethod({ notificationType: notificationType });
      refetchMe();
    }

    if (headContact) {
      await upsertHeadContact({ contact: headContact });
    }
  };

  const isDisabled = () => {
    if (
      updateUsernameStatus === "pending" ||
      updatePasswordStatus === "pending" ||
      setNotificationMethodStatus === "pending"
    ) {
      return true;
    }

    if (username !== user?.username) {
      return false;
    }

    if (password !== "" && password.length >= 6) {
      return false;
    }

    if (notificationType !== (userData?.tgIsPreferred ? "telegram" : "panel")) {
      return false;
    }

    if (!headContact || headContact === headContactFromApi?.data.data.contact) {
      return true;
    }
  };

  const updateStatus =
    updateUsernameStatus === "pending" ||
    updatePasswordStatus === "pending" ||
    setNotificationMethodStatus === "pending"
      ? "pending"
      : updateUsernameStatus === "success" ||
          updatePasswordStatus === "success" ||
          setNotificationMethodStatus === "success"
        ? "success"
        : "idle";

  return {
    username,
    setUsername,
    password,
    setPassword,
    notificationType,
    setNotificationType,
    handleUpdate,
    isDisabled,
    isBlocked,
    setIsBlocked,
    updateStatus,
    userData,
    toast,
    headContact,
    setHeadContact,
  };
};
